// surchargeApi — bảng giá phát sinh MỘT MỐI (bảng surcharge, migration 0200).
//
// Thay cho mảng `SURCHARGE_CATALOG` chép tay trong constants.ts: bảng đó và
// bản Go là hai bản chép tay của cùng một bảng giá, lệch nhau được mà không có
// gì đỏ. Từ giai đoạn 2, giá và phạm vi đến từ máy chủ.
//
// KHÔNG còn lưới đỡ (QĐ 0114): API lỗi thì bảng chọn TRỐNG kèm câu báo, không
// có giá nào bịa ra từ mã nguồn. Ba trạng thái, ba cách xử khác nhau:
//
//	isLoading      chưa có gì, đang tải     → "đang tải bảng giá…"
//	isUnavailable  lỗi VÀ chưa từng có data → bảng trống, khoá nút ghi
//	isStale        lỗi NHƯNG còn data cũ    → giữ bảng + dải "có thể cũ"
//
// Phân biệt hai cái sau là quan trọng: react-query v5 giữ `data` của lần đọc
// trước khi refetch hụt, nên xoá bảng lúc đó là làm biến mất DÒNG NGƯỜI DÙNG
// ĐANG CHỌN giữa chừng — họ bấm Lưu và đơn thiếu khoản (red-team 10/09 #12).
//
// ⚠️ Chỉ nhóm QUYẾT ĐỊNH TIỀN dùng hook này (bảng chọn phát sinh, trang Sản
// phẩm, tab giá ảnh CSKH). Nhóm CHỈ HIỂN THỊ TÊN dùng `useSurchargeLabel`
// (surchargeLabel.ts) — cũng đọc từ đây, nên đổi tên một mục là mọi nơi đổi
// theo, không còn cảnh thẻ lịch giữ tên cũ tới lần deploy sau.
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";
import type { SurchargeCatalogItem, SurchargeGroup, SurchargeKind } from "./constants";

export type ApiSurchargeItem = components["schemas"]["SurchargeCatalogItem"];
export type ApiSurchargeKind = components["schemas"]["SurchargeKind"];
export type ApiSurchargeScope = components["schemas"]["SurchargeScope"];
export type ApiLadderStep = components["schemas"]["SurchargeLadderStep"];
export type SurchargeCatalogResponse = components["schemas"]["SurchargeCatalogResponse"];

export const SURCHARGE_CATALOG_KEY = ["surcharges", "catalog"] as const;

/** Một bậc của bảng combo ảnh chỉnh sửa, đã ghép giá. */
export type LadderRow = {
  catalogId: string;
  /** Số tấm bậc này bao. null ở bậc ĐUÔI. */
  qty: number | null;
  isTail: boolean;
  label: string;
  /** Đơn giá của mục, null khi mục không có giá cố định. */
  price: number | null;
};

export type SurchargeCatalog = {
  items: SurchargeCatalogItem[];
  ladder: LadderRow[];
  /** Tấm đầu tiên ăn giá bậc đuôi — máy chủ suy ra, đừng tự tính lại. */
  tailFrom: number;
  /** Ba nhóm phát sinh kèm nhãn tiếng Việt, do máy chủ định nghĩa. */
  groups: ApiSurchargeGroup[];
  /** Nhóm mà CHÍNH người đang đăng nhập được thấy — máy chủ tính. */
  visibleGroups: SurchargeGroup[];
  /**
   * Sáu nhóm HIỂN THỊ kèm nhãn, đúng thứ tự bảng chọn gập/mở (QĐ 0130). Máy
   * chủ cũ chưa trả → mảng rỗng, bảng chọn rơi về thứ tự cứng SURCHARGE_KIND_ORDER.
   */
  kinds: ApiSurchargeKind[];
  /** Luôn 'db' — bảng trong CSDL là nguồn duy nhất (QĐ 0114). */
  source: "db";
};

export type ApiSurchargeGroup = components["schemas"]["SurchargeGroup"];

/**
 * Đổi mục của máy chủ sang hình dạng `SurchargeCatalogItem` mà mọi màn đang
 * dùng, để việc chuyển nguồn không kéo theo sửa từng chỗ hiển thị.
 *
 * Phạm vi: mảng rỗng ⇒ "all". Máy chủ dùng `line` cho thứ mà FE gọi là
 * `categories` — cùng giá trị (idphoto/profile/corporate), khác tên khái niệm
 * sau khi migration 0199 đổi sang `products.line_code`.
 */
export function toCatalogItem(it: ApiSurchargeItem): SurchargeCatalogItem {
  const lines = it.scopes.filter((s) => s.type === "line").map((s) => s.id);
  const products = it.scopes.filter((s) => s.type === "product").map((s) => s.id);
  const hasAll = it.scopes.length === 0 || it.scopes.some((s) => s.type === "all");

  return {
    id: it.id,
    group: it.group as SurchargeCatalogItem["group"],
    // Máy chủ cũ không có cột → undefined, bảng chọn xếp vào "Khác".
    kind: (it.kind as SurchargeKind | undefined) || undefined,
    label: it.label,
    // Giá thoả thuận và giá tính theo bậc thang đều để trống ở máy chủ; bảng
    // tĩnh biểu diễn cả hai bằng `null`.
    price: it.price ?? null,
    perUnit: it.perUnit || undefined,
    minQty: it.minQty || undefined,
    appliesTo: hasAll ? "all" : { categories: lines, products },
    hiddenFromPicker: it.hiddenFromPicker || undefined,
    cskhSellable: it.cskhSellable || undefined,
    needsMakeupCrew: it.needsMakeupCrew || undefined,
    // `?.` vì máy chủ cũ (trước 0466) chưa có trường này.
    addsMakeupFace: it.addsMakeupFace || undefined,
    lockedFromDisable: it.lockedFromDisable || undefined,
    isActive: it.isActive,
    systemKey: it.systemKey || undefined,
    fromGnwStock: it.source === "gnw" || undefined,
    sortOrder: it.sortOrder,
    // Quà tặng kèm (QĐ 0118). Giữ 0 thành undefined như các cờ trên: chỗ hiển
    // thị hỏi "có quà không", và `0` là không.
    giftEditPhotos: it.giftEditPhotos || undefined,
    giftPrints: it.giftPrints || undefined,
    giftNote: it.giftNote || undefined,
    giftItems: it.giftItems?.length ? it.giftItems : undefined,
  } as SurchargeCatalogItem;
}

function buildLadder(res: SurchargeCatalogResponse): LadderRow[] {
  const byID = new Map(res.items.map((it) => [it.id, it]));
  return res.ladder.map((step) => {
    const it = byID.get(step.surchargeId);
    return {
      catalogId: step.surchargeId,
      qty: step.qty ?? null,
      isTail: step.isTail,
      label: it?.label ?? step.surchargeId,
      price: it?.price ?? null,
    };
  });
}

async function fetchSurchargeCatalog(): Promise<SurchargeCatalogResponse> {
  const { data, error } = await api.GET("/surcharge-catalog");
  // Kiểm cả `data.items`: đáp ứng thiếu khoá (proxy trả khung rỗng, bản máy
  // chủ cũ) làm hàm trả undefined và react-query nổ ở console thay vì đi vào
  // nhánh lỗi — lúc đó lưới đỡ không bật, bảng giá trắng trơn.
  if (error || !data?.items) {
    throw new Error(serverErrorMessage(error, "Không đọc được bảng giá phát sinh"));
  }
  return data;
}

/**
 * Bảng giá phát sinh. `staleTime` 5 phút, cùng nhịp bộ nhớ đệm phía máy chủ —
 * đặt ngắn hơn chỉ làm tăng lượt gọi mà không tươi hơn.
 */
export function useSurchargeCatalog() {
  const q = useQuery({
    queryKey: SURCHARGE_CATALOG_KEY,
    queryFn: fetchSurchargeCatalog,
    staleTime: 5 * 60 * 1000,
  });

  const catalog = useMemo<SurchargeCatalog>(() => {
    if (!q.data) {
      return {
        items: [],
        ladder: [],
        tailFrom: 0,
        groups: [],
        visibleGroups: [],
        kinds: [],
        source: "db",
      };
    }
    return {
      items: q.data.items.map(toCatalogItem),
      ladder: buildLadder(q.data),
      tailFrom: q.data.tailFrom,
      groups: q.data.groups,
      visibleGroups: q.data.visibleGroups as SurchargeGroup[],
      // `?? []`: máy chủ cũ (trước 0261) chưa có trường này.
      kinds: q.data.kinds ?? [],
      source: "db",
    };
  }, [q.data]);

  // isUnavailable ≠ isStale, và trộn hai cái là mất dòng đang chọn của người
  // dùng: react-query v5 GIỮ `data` cũ khi refetch hụt, nên chỉ khi CHƯA TỪNG
  // đọc được mới coi là "không có bảng giá".
  const isUnavailable = q.isError && q.data === undefined;
  const isStale = q.isError && q.data !== undefined;

  return { ...q, catalog, isUnavailable, isStale };
}

/**
 * Nhóm phát sinh người đang đăng nhập được thấy, do MÁY CHỦ tính.
 *
 * Trước QĐ 0114 giao diện giữ bản chép bản đồ phòng→nhóm
 * (DEPT_SURCHARGE_GROUPS): hai bản của một luật, lệch nhau thì nhân viên thấy
 * nút bấm được nhưng bấm vào ăn 403.
 *
 * Chưa đọc được bảng giá → mảng RỖNG, và bảng chọn cũng trống theo: không có
 * bảng giá thì không có gì để chọn.
 */
export function useSurchargeGroups(): SurchargeGroup[] {
  const { catalog } = useSurchargeCatalog();
  return catalog.visibleGroups;
}

/**
 * Mục phát sinh liên quan tới một TAB loại sản phẩm trên trang Sản phẩm: mục
 * "mọi gói" + mục đúng dòng + mục gắn với bất kỳ gói nào thuộc dòng đó.
 *
 * KHÔNG lọc `hiddenFromPicker`: đây là bảng giá để xem và sửa, không phải bảng
 * chọn tay — giấu mục ẩn đi thì giám đốc không sửa được giá của nó.
 */
export function surchargesForCategoryIn(
  items: SurchargeCatalogItem[],
  categoryId: string,
  productIdsOfCategory: readonly string[],
): SurchargeCatalogItem[] {
  const ids = new Set(productIdsOfCategory);
  return items.filter((item) => {
    if (item.appliesTo === "all") return true;
    if (item.appliesTo.categories?.includes(categoryId)) return true;
    return item.appliesTo.products?.some((pid) => ids.has(pid)) ?? false;
  });
}

/**
 * Sáu nhóm hiển thị theo thứ tự bảng chọn — chỉ dùng khi máy chủ cũ chưa trả
 * `kinds`. Gương của `surchargeKindCodes` / `surchargeKindLabels` ở
 * backend/internal/core/order/surcharge_catalog.go; bình thường nhãn đến từ
 * máy chủ, bản này không phải nguồn.
 */
export const SURCHARGE_KIND_ORDER: readonly SurchargeKind[] = [
  "trang_diem",
  "toc",
  "trang_phuc",
  "nguoi",
  "anh",
  "my_pham",
  "khac",
];

const SURCHARGE_KIND_FALLBACK_LABEL: Record<SurchargeKind, string> = {
  trang_diem: "Trang điểm & làm đẹp",
  toc: "Tóc",
  trang_phuc: "Trang phục & phông nền",
  nguoi: "Người & concept",
  anh: "Ảnh: sửa – in – khung",
  my_pham: "Mỹ phẩm",
  khac: "Khác",
};

export type SurchargeKindGroup<T = SurchargeCatalogItem> = {
  code: SurchargeKind;
  label: string;
  items: T[];
};

/**
 * Sáu nhóm hiển thị kèm nhãn để bày ra (ô "Nhóm hiển thị" ở hộp Thêm/Sửa,
 * ngăn ở bảng chọn): ưu tiên `kinds` máy chủ trả, máy chủ cũ chưa trả thì
 * dùng thứ tự + nhãn dự phòng ở trên.
 */
export function surchargeKindOptions(
  kinds: readonly ApiSurchargeKind[],
): { code: SurchargeKind; label: string }[] {
  return kinds.length > 0
    ? kinds.map((k) => ({ code: k.code as SurchargeKind, label: k.label }))
    : SURCHARGE_KIND_ORDER.map((code) => ({ code, label: SURCHARGE_KIND_FALLBACK_LABEL[code] }));
}

/**
 * Chia mục theo nhóm HIỂN THỊ (`kind`, QĐ 0130) đúng thứ tự `kinds` máy chủ
 * trả; mục thiếu `kind` hay mang mã lạ rơi về "Khác"; nhóm rỗng bị bỏ (không
 * bày ngăn trống). Thứ tự mục trong nhóm giữ nguyên thứ tự đầu vào (= thứ tự
 * bảng giá). Không đụng quyền: lọc theo phòng ban (`group`) đã xong trước đó.
 */
export function groupSurchargesByKind<T extends Pick<SurchargeCatalogItem, "kind">>(
  items: readonly T[],
  kinds: readonly ApiSurchargeKind[],
): SurchargeKindGroup<T>[] {
  const order: SurchargeKindGroup<T>[] = surchargeKindOptions(kinds).map((k) => ({
    ...k,
    items: [] as T[],
  }));
  const known = new Set(order.map((k) => k.code));
  let other = order.find((k) => k.code === "khac");
  if (!other) {
    other = { code: "khac", label: SURCHARGE_KIND_FALLBACK_LABEL.khac, items: [] };
    order.push(other);
  }
  for (const it of items) {
    const code = it.kind && known.has(it.kind) ? it.kind : "khac";
    (order.find((k) => k.code === code) ?? other).items.push(it);
  }
  return order.filter((k) => k.items.length > 0);
}

/**
 * Mục phát sinh áp dụng cho một gói — bản nhận catalog làm tham số.
 *
 * Cùng luật với `AllowedForOrder` bên máy chủ: không phạm vi = mọi gói; có thì
 * phải khớp dòng HOẶC gói.
 */
export function surchargesForProductIn(
  items: SurchargeCatalogItem[],
  productId: string,
  category: string,
): SurchargeCatalogItem[] {
  return items.filter((item) => {
    if (item.hiddenFromPicker) return false;
    if (item.appliesTo === "all") return true;
    if (item.appliesTo.categories?.includes(category)) return true;
    return item.appliesTo.products?.includes(productId) ?? false;
  });
}

// ── Đường GHI (giai đoạn 3) ─────────────────────────────────────────
//
// Mọi mutation làm mới lại bảng giá sau khi ghi: giám đốc sửa xong phải THẤY
// ngay, không phải chờ hết 5 phút nhớ tạm.
//
// KHÔNG có hook xoá — không có cửa xoá. Ngừng bán mục cũ rồi tạo mục mới.

export type SurchargeCreateBody = components["schemas"]["SurchargeCatalogCreate"];
export type SurchargeUpdateBody = components["schemas"]["SurchargeCatalogUpdate"];

async function refreshPricing(qc: QueryClient) {
  await Promise.all([
    qc.invalidateQueries({ queryKey: SURCHARGE_CATALOG_KEY }),
    qc.invalidateQueries({ queryKey: ["cskh", "extra-photo-quote"] }),
  ]);
}

/** Thông điệp máy chủ nêu ĐÚNG lý do bị chặn — đừng nuốt, người sửa cần đọc. */
function loiMayChu(error: unknown, mac: string): Error {
  return new Error(serverErrorMessage(error, mac));
}

export function useCreateSurcharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SurchargeCreateBody) => {
      const { data, error } = await api.POST("/surcharge-catalog", { body });
      if (error || !data) throw loiMayChu(error, "Không thêm được mục");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SURCHARGE_CATALOG_KEY }),
  });
}

export function useUpdateSurcharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: SurchargeUpdateBody }) => {
      const { data, error } = await api.PUT("/surcharge-catalog/{id}", {
        params: { path: { id } },
        body,
      });
      if (error || !data) throw loiMayChu(error, "Không sửa được mục");
      return data;
    },
    onSuccess: () => refreshPricing(qc),
  });
}

export function useSetSurchargeActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { data, error } = await api.PATCH("/surcharge-catalog/{id}/active", {
        params: { path: { id } },
        body: { isActive },
      });
      if (error || !data) throw loiMayChu(error, "Không đổi được trạng thái bán");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SURCHARGE_CATALOG_KEY }),
  });
}

export function useSetSurchargeScopes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, scopes }: { id: string; scopes: ApiSurchargeScope[] }) => {
      const { data, error } = await api.PUT("/surcharge-catalog/{id}/scope", {
        params: { path: { id } },
        body: { scopes },
      });
      if (error || !data) throw loiMayChu(error, "Không đặt được phạm vi");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SURCHARGE_CATALOG_KEY }),
  });
}

/**
 * Đặt danh sách món đồ tặng kèm một mục (QĐ 0118).
 *
 * Cửa riêng chứ không nằm trong PUT /:id, cùng khuôn với phạm vi áp dụng: danh
 * sách món là một TẬP, thay trọn chứ không vá lẻ.
 */
export function useSetSurchargeGiftItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, giftItemIds }: { id: string; giftItemIds: string[] }) => {
      const { data, error } = await api.PUT("/surcharge-catalog/{id}/gift-items", {
        params: { path: { id } },
        body: { giftItemIds },
      });
      if (error || !data) throw loiMayChu(error, "Không đặt được món tặng");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SURCHARGE_CATALOG_KEY }),
  });
}

export function useSetLadder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (steps: ApiLadderStep[]) => {
      const { data, error } = await api.PUT("/surcharge-catalog/ladder", {
        body: { steps },
      });
      if (error || !data) throw loiMayChu(error, "Không đặt được bậc thang");
      return data;
    },
    onSuccess: () => refreshPricing(qc),
  });
}
